import { defineStore } from 'pinia';
import router from '@/router';
import apiService from '../services/api.service';
import { hasAdminPortalAccess } from '@/constants/roles';
import type { AxiosError } from 'axios';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  role?: { name: string };
}

interface Credentials {
  email: string;
  password?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  status: string;
}

interface ApiErrorResponse {
  message: string;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    isAuthenticated: false,
    status: 'idle',
  }),
  getters: {
    isUserLoggedIn: (state: AuthState): boolean => state.isAuthenticated,
    getUser: (state: AuthState): User | null => state.user,
    getAuthStatus: (state: AuthState): string => state.status,
  },
  actions: {
    clearSession(shouldRedirect = false) {
      this.user = null;
      this.isAuthenticated = false;
      this.status = 'idle';
      localStorage.removeItem('user');
      if (shouldRedirect) {
        router.push('/login');
      }
    },
    async login(credentials: Credentials) {
      this.status = 'loading';
      try {
        const response = await apiService.post<{ data: { user: User } }>('/auth/login', credentials);
        const user = response.data.data.user;
        this.user = user;
        this.isAuthenticated = true;
        localStorage.setItem('user', JSON.stringify(user));
        this.status = 'success';

        const userRole = this.user?.role?.name;
        if (userRole && hasAdminPortalAccess(userRole)) {
          router.push('/admin/appointments');
        } else {
          router.push('/');
        }
      } catch (error) {
        this.status = 'error';
        this.clearSession(false);
        const axiosError = error as AxiosError<ApiErrorResponse>;
        throw new Error(axiosError.response?.data?.message || axiosError.message || 'Login failed');
      }
    },
    async register(userData: Partial<User> & { password?: string }) {
      this.status = 'loading';
      try {
        const response = await apiService.post<{ data: { user: User } }>('/auth/register', userData);
        this.user = response.data.data.user;
        this.isAuthenticated = true;
        localStorage.setItem('user', JSON.stringify(this.user));
        this.status = 'success';
        router.push({ name: 'login', query: { registered: 'true' } });
      } catch (error) {
        this.status = 'error';
        const axiosError = error as AxiosError<ApiErrorResponse>;
        throw new Error(axiosError.response?.data?.message || axiosError.message || 'Registration failed');
      }
    },
    async logout(shouldRedirect = true) {
      try {
        await apiService.post('/auth/logout');
      } catch {
        // Ignore — session may already be expired
      }
      this.clearSession(shouldRedirect);
    },
    async checkAuth() {
      try {
        const response = await apiService.get<{ data: User }>('/auth/me');
        this.user = response.data.data;
        this.isAuthenticated = true;
        localStorage.setItem('user', JSON.stringify(this.user));
      } catch {
        this.clearSession(false);
      }
    },
  },
});
